import { useState } from "react";
import { motion } from "framer-motion";
import { colors } from "../constants";
import GlassCard from "./GlassCard";

const STORAGE_KEY = "livePredictionsInfoCardSeen";

const LivePredictionsInfoCard = ({ onLearnMore }) => {
  // Mount-only read so the card doesn't flicker in/out on re-renders.
  const [visible] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) !== "1";
    } catch (_) {
      return true;
    }
  });

  if (!visible) return null;

  const handleLearnMore = () => {
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch (_) {
      /* localStorage disabled — card will still retire for this session */
    }
    onLearnMore?.();
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="mb-4"
      aria-labelledby="live-predictions-info-heading"
    >
      <GlassCard padding="p-4">
        {/* Header row: LIVE pulse + headline */}
        <div className="flex items-center gap-2 mb-1" aria-hidden="true">
          <span className="relative flex h-2 w-2">
            <span
              className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
              style={{ background: colors.accent }}
            />
            <span
              className="relative inline-flex rounded-full h-2 w-2"
              style={{ background: colors.accent }}
            />
          </span>
          <span
            className="text-[10px] font-bold tracking-widest uppercase"
            style={{ color: colors.accent }}
          >
            Live
          </span>
          <span className="text-[10px] font-bold tracking-widest uppercase text-white/40">
            · New
          </span>
        </div>
        <p
          id="live-predictions-info-heading"
          className="text-base font-semibold text-white mb-1"
        >
          Predict as the match unfolds
        </p>
        <p className="text-sm text-white/60 mb-4">
          Score on every over, not just the final result.
        </p>

        {/* Teaser: illustrative mock question (not the real mechanic) */}
        <div
          className="rounded-xl p-3 mb-3"
          style={{
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.08)",
          }}
          aria-hidden="true"
        >
          <p className="text-xs text-white/50 mb-2">Next over: how many runs?</p>
          <div className="flex gap-2">
            {["<5", "5–9", "10+"].map((label) => (
              <div
                key={label}
                className="flex-1 py-2 rounded-lg text-center text-sm font-semibold text-white/80"
                style={{
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.1)",
                }}
              >
                {label}
              </div>
            ))}
          </div>
        </div>

        {/* Footer CTA */}
        <button
          type="button"
          onClick={handleLearnMore}
          data-testid="live-predictions-learn-more"
          className="text-sm font-semibold hover:opacity-80 transition-opacity"
          style={{ color: colors.accent }}
        >
          Learn how it works <span aria-hidden="true">→</span>
        </button>
      </GlassCard>
    </motion.section>
  );
};

export default LivePredictionsInfoCard;
